import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Heart, Star, Check, Sparkle } from '@phosphor-icons/react';
import ShareCardModal from './ShareCardModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  FAN:    { icon: Heart,   accent: 'border-[#f472b6]/40 from-[#f472b6]/12',  text: 'text-[#f472b6]' },
  SUPER:  { icon: Star,    accent: 'border-[#c084fc]/40 from-[#c084fc]/12',  text: 'text-[#c084fc]' },
  PATRON: { icon: Crown,   accent: 'border-[#facc15]/40 from-[#facc15]/12',  text: 'text-[#facc15]' },
};

/**
 * Backstage Pass tier selector — drops onto MovieDetail.
 * Props: movieId, movieTitle, user (current logged-in user)
 */
const BackstagePassPanel = ({ movieId, movieTitle, user }) => {
  const [tiers, setTiers] = useState(null);
  const [mine, setMine] = useState(null);
  const [busyTier, setBusyTier] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTier, setShareTier] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/backstage/tiers`).then((r) => setTiers(r.data.tiers)).catch(() => {});
    const token = localStorage.getItem('token');
    if (token && movieId) {
      axios.get(`${BACKEND_URL}/api/backstage/movie/${movieId}/mine`,
        { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setMine(r.data.pass))
        .catch(() => {});
    }
  }, [movieId]);

  const purchase = async (tierKey) => {
    if (!user) { toast.error('Sign in to grab a Backstage Pass'); return; }
    setBusyTier(tierKey);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/backstage/purchase`,
        { movie_id: movieId, tier: tierKey },
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.already_owned) {
        toast.info("You already own a pass for this film");
      } else {
        const z = res.data.zinglets_awarded;
        toast.success(`${tierKey} pass unlocked${z ? ` · +${z} Zinglet${z > 1 ? 's' : ''}` : ''}`);
        setMine(res.data.pass);
        setShareTier(tierKey);
        setShareOpen(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete purchase');
    } finally {
      setBusyTier(null);
    }
  };

  if (!tiers) return null;
  const ownsTier = mine?.tier;
  const tierOrder = ['FAN', 'SUPER', 'PATRON'];

  return (
    <section className="bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-transparent border border-white/12 rounded-2xl p-5 sm:p-6" data-testid="backstage-panel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={16} weight="fill" className="text-[#facc15]" />
        <h3 className="text-base font-extrabold tracking-tight">Backstage Pass</h3>
        {ownsTier && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10px] uppercase tracking-wider text-emerald-300 font-bold" data-testid="backstage-owned-pill">
            <Check size={10} weight="bold" /> Owned · {ownsTier}
          </span>
        )}
      </div>
      <p className="text-xs text-white/70 font-light mb-4">
        Not ready to invest? Become a Backstage fan — exclusive content + premiere lottery + Zinglet bonus. Starts at ₹49.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="backstage-tier-grid">
        {tierOrder.map((key) => {
          const t = tiers[key];
          if (!t) return null;
          const meta = TIER_META[key];
          const Icon = meta.icon;
          const owned = ownsTier === key;
          const lowerOwned = ownsTier && tierOrder.indexOf(ownsTier) > tierOrder.indexOf(key);
          return (
            <div
              key={key}
              className={`relative bg-gradient-to-b ${meta.accent} to-transparent border ${owned ? 'border-emerald-500/60' : ''} rounded-xl p-4 flex flex-col`}
              data-testid={`backstage-tier-${key}`}
            >
              <div className={`flex items-center gap-2 mb-1 ${meta.text}`}>
                <Icon size={18} weight="fill" />
                <div className="font-extrabold text-sm uppercase tracking-wider">{key}</div>
              </div>
              <div className="text-2xl font-extrabold tabular-nums mb-3" style={{ letterSpacing: '-0.02em' }}>
                ₹{t.amount_inr}
              </div>
              <ul className="space-y-1.5 mb-4 flex-1">
                {t.perks.map((p, i) => (
                  <li key={i} className="text-[11px] text-white/75 leading-snug flex items-start gap-1.5">
                    <Check size={11} weight="bold" className={`${meta.text} mt-0.5 flex-shrink-0`} /> {p}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`backstage-buy-${key}`}
                disabled={busyTier === key || owned || lowerOwned}
                onClick={() => purchase(key)}
                className={`w-full text-xs font-extrabold rounded-lg py-2.5 transition-all ${
                  owned ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 cursor-default' :
                  lowerOwned ? 'bg-white/5 border border-white/10 text-white/70 cursor-not-allowed' :
                  'bg-white text-black hover:scale-105'
                }`}
              >
                {owned ? 'Owned ✓' : lowerOwned ? 'Already on a higher tier' : busyTier === key ? 'Unlocking…' : `Unlock ${key}`}
              </button>
            </div>
          );
        })}
      </div>

      <ShareCardModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        movieId={movieId}
        movieTitle={movieTitle}
        name={user?.name}
        tier={shareTier}
      />
    </section>
  );
};

export default BackstagePassPanel;
